import { DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, Icon, TextField, Typography } from '@mui/material';
import CustomDialog from 'components/custom/CustomDialog';
import { CustomButton } from 'components/roundedbutton/CustomButton';
import useCatchApiError from 'hooks/useCatchApiError';
import { useFormValidator, useValidators } from 'hooks/useFormValidator';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { FormEvent, useState } from 'react';
import { Tipo, completeLoading, loadingWithOpt } from 'redux/features/blockuiSlice';
import { useAppDispatch } from 'redux/hooks';
import VendaService from '../venda.service';

type Props = {
    open: boolean;
    onClose: () => void;
    item: {
        serie: string;
        numeroNotaFiscal: number | string;
        cliente?: string;
        chaveAcesso: string;
        modelo?: string;
        tipoNotaFiscal: 'ENTRADA_MANUAL_PROPRIA' | 'VENDA' | 'VENDA_CONSUMIDOR' | 'DEVOLUCAO_VENDA';
    } | null | undefined;
};

const initialDialogState = {
    submitted: false,
};

const CancelaDFeDialog = (props: Props) => {
    const service = VendaService();
    const dispatch = useAppDispatch();
    const { addSuccess } = useSnackbarCustom();
    const [justificativaStateDialog, setJustificativaStateDialog] = useState(initialDialogState);
    const { minSize } = useValidators();
    const { hasError, getRawValue, formIsValid, resetControl, setControlValue, } =
        useFormValidator<{ justificativa: string | null; }>({
            controls: [{ inputName: 'justificativa', value: '', nonNullable: true, validators: [minSize(15)] },]
        });
    const { throwErrorMessage } = useCatchApiError();

    const onCloseDialog = () => {
        props.onClose();
        setJustificativaStateDialog(initialDialogState);
        resetControl('justificativa');
    };

    const onConfirmDialog = (e: FormEvent) => {
        e.preventDefault();
        setJustificativaStateDialog(prev => ({ ...prev, submitted: true }));
        if (!formIsValid()) return;

        dispatch(loadingWithOpt({
            loading: true,
            tipo: Tipo.CIRCULAR,
            zIndexCustom: 1000
        }));

        service.cancelarDFe(props.item!.chaveAcesso, getRawValue('justificativa'), props.item!.tipoNotaFiscal)
            .then(({ data }) => {
                addSuccess({ message: data, persist: false, });
                onCloseDialog();
            })
            .catch(err => {
                throwErrorMessage(err);
            })
            .finally(() => {
                dispatch(completeLoading());
            });
    };

    if (!props.item) return <></>;

    return (
        <CustomDialog
            id="justificativa-dialog"
            open={props.open}
            onClose={onCloseDialog}
            fullWidth={true}
            maxWidth={'sm'}
        >

            <form onSubmit={onConfirmDialog} style={{ gap: 0 }}>
                <DialogTitle id="dialog-title" textAlign={'center'}>
                    <Typography fontSize={30} paddingTop={0}>Cancelar {props.item.modelo ?? 'documento fiscal'}</Typography>
                </DialogTitle>

                <DialogContent id="dialog-content" sx={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography>Série:</Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Typography>{props.item.serie}</Typography>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Typography>Número:</Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Typography>{props.item.numeroNotaFiscal}</Typography>
                        </Grid>

                        {props.item.cliente && <>
                            <Grid item xs={12} md={3}>
                                <Typography>Cliente:</Typography>
                            </Grid>
                            <Grid item xs={12} md={9}>
                                <Typography>{props.item.cliente}</Typography>
                            </Grid>
                        </>
                        }

                        <Grid item xs={12}>
                            <FormControl fullWidth variant="standard">
                                <TextField
                                    label="Justificativa *"
                                    id={`justificativa-input`}
                                    fullWidth
                                    variant="standard"
                                    value={getRawValue('justificativa')}
                                    onChange={(e) => {
                                        setControlValue('justificativa', e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    error={justificativaStateDialog.submitted && hasError('minSize15', 'justificativa')}
                                />

                                {justificativaStateDialog.submitted && hasError('minSize15', 'justificativa') &&
                                    <FormHelperText
                                        className='Mui-error'
                                        id={`justificativa-helper-text`}
                                        color={'error'}
                                    >
                                        Valor inválido
                                    </FormHelperText>
                                }
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: 'space-between'
                    }}>
                    <CustomButton onClick={onCloseDialog} variant="contained" color="error" startIcon={<Icon className='fa-solid fa-xmark' />}>
                        Fechar
                    </CustomButton>
                    <CustomButton type="submit" disabled={false} variant="contained" color="success" startIcon={<Icon className='fa-solid fa-check' />}>
                        Confirmar
                    </CustomButton>
                </DialogActions>
            </form>
        </CustomDialog >
    );
};

export default CancelaDFeDialog;
